import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Link2, RefreshCw, ChevronDown, ChevronUp, Copy, ExternalLink,
  Clock, CheckCircle2, XCircle, Ban, AlertCircle,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const money = (n) => (n == null ? "—" : `₹${fmt(Math.round(n))}`);

const sentOn = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

const expiresIn = (secs) => {
  if (secs == null) return "no expiry set";
  if (secs <= 0) return "expired";
  const h = Math.floor(secs / 3600);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.max(1, Math.floor(secs / 60))}m left`;
};

const STATES = [
  { key: "sent", label: "Sent", icon: Link2, tone: "text-slate-700", ring: "border-slate-200", bg: "bg-white" },
  { key: "pending", label: "Pending", icon: Clock, tone: "text-amber-700", ring: "border-amber-200", bg: "bg-amber-50" },
  { key: "paid", label: "Paid", icon: CheckCircle2, tone: "text-emerald-700", ring: "border-emerald-200", bg: "bg-emerald-50" },
  { key: "expired", label: "Expired", icon: XCircle, tone: "text-rose-700", ring: "border-rose-200", bg: "bg-rose-50" },
  { key: "cancelled", label: "Cancelled", icon: Ban, tone: "text-slate-500", ring: "border-slate-200", bg: "bg-slate-50" },
];

/**
 * Payment Links — every link SENT in the selected range and where it stands now.
 * Pending is the actionable one, so its tile opens a chase-list with the
 * customer, the rep who sent it and how long the link has left.
 */
export default function ServicePaymentLinks({ period }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqRef.current;        // editing start then end fires two fetches
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/services/payment-links`, {
        params: { period },
      });
      if (seq !== reqRef.current) return;   // a newer range already won
      setData(res.data);
      setError("");
    } catch (e) {
      if (seq !== reqRef.current) return;
      setData(null);
      setError(e.response?.data?.detail || "Could not load payment links");
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const copy = async (url, name) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link copied — ${name}`);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const d = data || {};
  const sent = d.sent?.count || 0;
  const pending = d.pending_list || [];

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="svc-paylinks">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-slate-100 rounded-md"><Link2 className="w-4 h-4 text-slate-600" strokeWidth={2} /></div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800 tracking-tight">Payment Links</h3>
            <p className="text-[11px] text-slate-400" data-testid="svc-paylinks-subtitle">
              {sent > 0
                ? `${fmt(sent)} sent in this range · ${d.conversion_pct || 0}% paid${d.pending?.count ? ` · ${fmt(d.pending.count)} still waiting` : ""}`
                : "nothing sent in this range yet"}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md border border-slate-200 disabled:opacity-50"
          data-testid="svc-paylinks-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STATES.map(({ key, label, icon: Icon, tone, ring, bg }) => {
            const b = d[key] || {};
            const clickable = key === "pending" && (b.count || 0) > 0;
            return (
              <button
                key={key}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && setOpen((v) => !v)}
                className={`text-left rounded-lg border ${ring} ${bg} px-3 py-2.5 transition-shadow ${
                  clickable ? "hover:shadow-md cursor-pointer" : "cursor-default"
                }`}
                data-testid={`svc-paylinks-${key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</span>
                  <Icon className={`w-3.5 h-3.5 ${tone}`} strokeWidth={2} />
                </div>
                <div className={`text-2xl font-bold ${tone} leading-tight tabular-nums`}>{fmt(b.count)}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {d.redacted ? "amount hidden" : money(b.amount)}
                  {clickable && <span className="ml-1 text-amber-700 font-medium">{open ? "· hide" : "· chase"}</span>}
                </div>
              </button>
            );
          })}
        </div>

        {open && pending.length > 0 && (
          <div className="mt-4 border border-amber-200 rounded-lg overflow-hidden" data-testid="svc-paylinks-pending-list">
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs font-semibold text-amber-900">
                Waiting on the customer — {fmt(d.pending_total)} link{d.pending_total === 1 ? "" : "s"}
                {d.pending_scope === "own" && <span className="font-normal"> (your leads)</span>}
                {d.pending_without_link > 0 && (
                  <span className="ml-2 font-normal text-amber-700" data-testid="svc-paylinks-nolink-note">
                    · {fmt(d.pending_without_link)} have no link to chase
                  </span>
                )}
              </span>
              <button onClick={() => setOpen(false)} className="text-amber-700 hover:text-amber-900" data-testid="svc-paylinks-collapse">
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Customer</th>
                    <th className="text-left font-medium px-3 py-2">Car / Package</th>
                    <th className="text-left font-medium px-3 py-2">Rep</th>
                    <th className="text-right font-medium px-3 py-2">Amount</th>
                    <th className="text-left font-medium px-3 py-2">Sent</th>
                    <th className="text-left font-medium px-3 py-2">Expiry</th>
                    <th className="text-right font-medium px-3 py-2">Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pending.map((r) => (
                    <tr key={r.lead_id} className="hover:bg-amber-50/40" data-testid={`svc-paylinks-row-${r.lead_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{r.customer}</div>
                        <div className="text-[11px] text-slate-400">{r.mobile}{r.city ? ` · ${r.city}` : ""}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-700">{r.car || "—"}</div>
                        <div className="text-[11px] text-slate-400">{r.package || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{r.rep}</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-800 tabular-nums">{money(r.amount)}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{sentOn(r.sent_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={r.expires_in_seconds != null && r.expires_in_seconds < 21600
                          ? "text-rose-600 font-medium" : "text-slate-500"}>
                          {expiresIn(r.expires_in_seconds)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1.5">
                          {r.link_url ? (
                            <>
                              <button onClick={() => copy(r.link_url, r.customer)}
                                className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
                                title="Copy link" data-testid={`svc-paylinks-copy-${r.lead_id}`}>
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              <a href={r.link_url} target="_blank" rel="noreferrer"
                                className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
                                title="Open link" data-testid={`svc-paylinks-open-${r.lead_id}`}>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            </>
                          ) : <span className="text-[11px] text-slate-400">no link</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.pending_list_truncated && (
              <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
                Showing the {pending.length} most recent of {fmt(d.pending_total)} pending links.
              </div>
            )}
          </div>
        )}

        {!open && (d.pending?.count || 0) > 0 && (
          <button
            onClick={() => setOpen(true)}
            className="mt-3 flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900"
            data-testid="svc-paylinks-expand"
          >
            <ChevronDown className="w-3.5 h-3.5" />
            See who still has to pay
          </button>
        )}

        {sent === 0 && !loading && !error && (
          <p className="mt-4 text-sm text-slate-400" data-testid="svc-paylinks-empty">
            No payment links went out in this range.
          </p>
        )}

        {error && !loading && (
          <div className="mt-4 flex items-center gap-2 text-sm text-rose-600" data-testid="svc-paylinks-error">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
            <button onClick={load} className="underline underline-offset-2 font-medium" data-testid="svc-paylinks-retry">
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
